import React, { useReducer } from 'react';
import { Button, Modal } from 'rsuite';
import { useStoreActions } from '../../../store/hooks';
import { unholdOffers } from '../../../api/hold/hold';
import { showSuccessMessage } from '../../../utils/message';

interface Props {
  offerId: number;
  holdReason?: string;
  marketplace: string;
  close: () => void;
}

interface State {
  status: 'ready' | 'saving';
}

const initialState: State = {
  status: 'ready',
};

type UnholdSaveAction = { type: 'unhold/save' };
type Action = UnholdSaveAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'unhold/save':
      return { ...state, status: 'saving' };
    default:
      return state;
  }
}

export default function ProductUnholdDialog({
  offerId, holdReason, marketplace, close,
}: Props): JSX.Element {
  const { reloadProducts } = useStoreActions(actions => actions.products);
  const [state, dispatch] = useReducer(reducer, initialState);
  const isSaving = state.status === 'saving';

  async function handleUnhold(): Promise<void> {
    dispatch({ type: 'unhold/save' });
    try {
      await unholdOffers({ offers: [{ id: offerId }] });
      await reloadProducts();
      showSuccessMessage('Offer was unheld.');
    } finally {
      close();
    }
  }

  return (
    <Modal
      size="xs"
      open
      onClose={close}
    >
      <Modal.Header>
        <Modal.Title>Unhold offer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {`Are you sure you want to unhold the offer? It will be offered on ${marketplace} again.`}
        {holdReason && (
          <p className="mt-10">
            Current hold reason is
            {' '}
            <b>{holdReason}</b>
            .
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => handleUnhold()}
          disabled={isSaving}
          loading={isSaving}
          appearance="primary"
        >
          Unhold
        </Button>
        <Button
          onClick={close}
          disabled={isSaving}
          appearance="subtle"
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ProductUnholdDialog.defaultProps = {
  holdReason: undefined,
};
